import React, { useState, useEffect, useCallback } from 'react';
import apiKeyService from '../services/apiKeyService';
import roleService from '../services/roleService'; // To get the list of permissions
import { format } from 'date-fns';
import { FiPlusCircle, FiTrash2, FiCopy } from 'react-icons/fi';

const ApiKeysPage = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newKeyData, setNewKeyData] = useState({ name: '', permissions: [] });
  const [generatedKey, setGeneratedKey] = useState(null);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [keys, perms] = await Promise.all([
        apiKeyService.getApiKeys(),
        roleService.getPermissions()
      ]);
      setApiKeys(keys);
      setPermissions(perms);
    } catch (err) {
      setError('Failed to load data.');
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePermissionChange = (e) => {
    const { value, checked } = e.target;
    setNewKeyData(prev => {
      const newPermissions = checked
        ? [...prev.permissions, value]
        : prev.permissions.filter(p => p !== value);
      return { ...prev, permissions: newPermissions };
    });
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    try {
      const res = await apiKeyService.createApiKey(newKeyData);
      setGeneratedKey(res.key);
      setModalError('');
      fetchData();
    } catch (err) {
      setModalError(err.response?.data?.message || 'Failed to create key.');
    }
  };

  const handleRevokeKey = async (id) => {
    if (window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      try {
        await apiKeyService.revokeApiKey(id);
        fetchData();
      } catch (err) {
        setError('Failed to revoke key.');
      }
    }
  };

  const openModal = () => {
    setNewKeyData({ name: '', permissions: [] });
    setGeneratedKey(null);
    setError('');
    setModalError('');
    setIsModalOpen(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('API Key copied to clipboard!');
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">API Key Management</h1>
        <button onClick={openModal} className="flex items-center bg-primary text-white px-4 py-2 rounded-md shadow hover:bg-primary-dark">
          <FiPlusCircle className="mr-2" /> Generate New Key
        </button>
      </div>
      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
      <div className="bg-white shadow-md rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Prefix</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Last Used</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {apiKeys.map(key => (
              <tr key={key._id}>
                <td className="px-4 py-4 font-semibold">{key.name}</td>
                <td className="px-4 py-4 font-mono text-sm">{key.prefix}_...</td>
                <td className="px-4 py-4"><span className={`px-2 inline-flex text-xs font-semibold rounded-full ${key.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{key.isActive ? 'Active' : 'Revoked'}</span></td>
                <td className="px-4 py-4 text-sm">{key.lastUsed ? format(new Date(key.lastUsed), 'dd-MM-yyyy') : 'Never'}</td>
                <td className="px-4 py-4 text-right">
                  {key.isActive && <button onClick={() => handleRevokeKey(key._id)} className="text-red-600 hover:text-red-900"><FiTrash2 /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl">
            <h2 className="text-2xl font-bold mb-4">Generate New API Key</h2>
            {modalError && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{modalError}</p>}
            {generatedKey ? (
              <div>
                <p className="text-sm text-gray-600 mb-2">Please copy this key and store it securely. You will not be able to see it again.</p>
                <div className="bg-gray-100 p-3 rounded-md flex items-center justify-between font-mono">
                  <span>{generatedKey}</span>
                  <button onClick={() => copyToClipboard(generatedKey)} className="text-gray-600 hover:text-blue-600"><FiCopy /></button>
                </div>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setModalError('');
                  }}
                  className="mt-6 w-full bg-primary text-white py-2 rounded-md"
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleCreateKey} className="space-y-4">
                <input name="name" value={newKeyData.name} onChange={(e) => setNewKeyData({...newKeyData, name: e.target.value})} placeholder="Key Name (e.g., Reporting Dashboard)" required className="p-2 border rounded-md w-full" />
                <div className="border p-4 rounded-md max-h-64 overflow-y-auto">
                  <h3 className="font-semibold mb-2">Permissions</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.values(permissions).map(p => (
                      <label key={p} className="flex items-center text-sm"><input type="checkbox" value={p} checked={newKeyData.permissions.includes(p)} onChange={handlePermissionChange} className="mr-2" /> {p}</label>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false);
                      setModalError('');
                    }}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="bg-primary text-white px-4 py-2 rounded-md">Generate Key</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeysPage;
